import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Phone, Mail, MapPin, MessageCircle, Send, Clock, Instagram, Linkedin, Facebook } from 'lucide-react';
import { mockCompanyInfo } from '../mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch('https://api.gheikaingenieria.com/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        toast({
          title: t('contact.form.success.title'),
          description: t('contact.form.success.description'),
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          message: ''
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to send message',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred',
        variant: 'destructive',
      });
    }
    setIsSubmitting(false);
  };

  const contactMethods = [
    {
      icon: Phone,
      title: t('contact.methods.phone'),
      value: mockCompanyInfo.contact.phone,
      action: `tel:3017891934`
    },
    {
      icon: Mail,
      title: t('contact.methods.email'),
      value: mockCompanyInfo.contact.email,
      action: `mailto:${mockCompanyInfo.contact.email}`
    },
    {
      icon: MessageCircle,
      title: t('contact.methods.whatsapp'),
      value: mockCompanyInfo.contact.whatsapp,
      action: `https://wa.me/${mockCompanyInfo.contact.whatsapp.replace(/[\s\-\(\)]/g, '')}?text=${encodeURIComponent(t('contact.whatsappMessage'))}`
    },
    {
      icon: MapPin,
      title: t('contact.methods.address'),
      value: t('company.address'),
      action: 'https://maps.app.goo.gl/7cXgSg3wAiL2VYfk9'
    }
  ];

  return (
    <section id="contacto" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('contact.title')}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('contact.description')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              <div className="text-center lg:text-left">
                <h3 className="text-2xl font-bold text-white mb-4">
                  {t('contact.info.title')}
                </h3>
                <p className="text-gray-400 mb-8">
                  {t('contact.info.description')}
                </p>
              </div>

              {/* Contact Methods */}
              <div className="space-y-4">
                {contactMethods.map((method, index) => {
                  const IconComponent = method.icon;
                  return (
                    <a
                      key={index}
                      href={method.action}
                      {...(method.action.startsWith('http') || method.action.startsWith('mailto') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      className="flex items-center space-x-4 p-4 bg-gray-800 border border-gray-700 rounded-lg hover:border-red-500 transition-all duration-300 hover:shadow-lg group"
                    >
                      <div className="p-3 bg-red-600/20 rounded-lg text-red-500 group-hover:bg-red-600 group-hover:text-white transition-all duration-300">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="font-semibold text-white group-hover:text-red-500 transition-colors duration-300">
                          {method.title}
                        </div>
                        <div className="text-gray-400 text-sm">
                          {method.value}
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>

              {/* Business Hours */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-white">
                    <Clock className="w-5 h-5 text-red-500" />
                    <span>{t('contact.hours.title')}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-gray-300">
                  <div className="flex justify-between">
                    <span>{t('contact.hours.weekdays')}</span>
                    <span>8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('contact.hours.saturday')}</span>
                    <span>9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>{t('contact.hours.sunday')}</span>
                    <span>{t('contact.hours.closed')}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Social Media */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">
                    {t('contact.social.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 text-sm mb-4">
                    {t('contact.social.description')}
                  </p>
                  <div className="flex space-x-4">
                    <a
                      href={mockCompanyInfo.contact.socialMedia.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center p-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg text-white hover:from-purple-700 hover:to-pink-700 transition-all duration-300 hover:shadow-lg"
                      title="Instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a
                      href={mockCompanyInfo.contact.socialMedia.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center p-3 bg-blue-600 rounded-lg text-white hover:bg-blue-700 transition-all duration-300 hover:shadow-lg"
                      title="LinkedIn"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-2xl text-white">
                  {t('contact.form.title')}
                </CardTitle>
                <CardDescription className="text-gray-400 text-base">
                  {t('contact.form.description')}
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white">{t('contact.form.fields.name')}</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 focus:border-red-500"
                        placeholder={t('contact.form.placeholders.name')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">{t('contact.form.fields.email')}</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 focus:border-red-500"
                        placeholder={t('contact.form.placeholders.email')}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white">{t('contact.form.fields.phone')}</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 focus:border-red-500"
                        placeholder={t('contact.form.placeholders.phone')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="projectType" className="text-white">{t('contact.form.fields.projectType')}</Label>
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="">{t('contact.form.options.select')}</option>
                        <option value="residential">{t('contact.form.options.residential')}</option>
                        <option value="commercial">{t('contact.form.options.commercial')}</option>
                        <option value="industrial">{t('contact.form.options.industrial')}</option>
                        <option value="soilStudy">{t('contact.form.options.soilStudy')}</option>
                        <option value="vulnerability">{t('contact.form.options.vulnerability')}</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">{t('contact.form.fields.message')}</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 focus:border-red-500 resize-none"
                      placeholder={t('contact.form.placeholders.message')}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-4 text-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        {t('contact.form.sending')}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        {t('contact.form.button')}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;